import React, { useState } from 'react';
import { Check, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getStatusColor, getDisplayColor } from '@/lib/statusColors';
import { useConfig } from '@/context/ConfigContext';
import { useSubscription } from '@/context/SubscriptionContext';
import { useTheme } from '@/context/ThemeContext';
import { createStatusPushEvent } from '@/services/pushEvents';
import { getOrCreateDeviceId } from '@/services/pushNotifications';

export function RouteStatusBar({ 
  currentStatus, 
  onStatusChange, 
  currentDate, 
  selectedUser,
  profileName = null,
  statusLabels,
  requireConfirmation = false,
  profileUuid = null
}) {
  const { config, workspaceId } = useConfig();
  const { permissions } = useSubscription();
  const { theme } = useTheme();

  // Estado de confirmación pendiente para cambios de ruta (independiente de marcado de clientes)
  // null = sin confirmación pendiente | string = key del estado pendiente de confirmación
  const [pendingRouteStatus, setPendingRouteStatus] = useState(null);

  // ── Calcular opciones y estado activo ANTES de usarlos en funciones ──

   const statusColors = (config?.routeStatesConfig?.colors) || { go: 'green', salimos: 'blue', volvemos: 'yellow', fin: 'red' };

  const routeStatesConfig = config?.routeStatesConfig || { mode: '4', labelsFor2States: { state1: 'Go', state2: 'Fin' } };
  
  const labels = statusLabels || {
    go: 'Go',
    salimos: 'Salimos',
    volvemos: 'Volvemos',
    fin: 'Fin',
  };

  let statusOptions = [];
  
  if (routeStatesConfig.mode === '2') {
    const s1Label = routeStatesConfig.labelsFor2States?.state1 || 'Go';
    const s2Label = routeStatesConfig.labelsFor2States?.state2 || 'Fin';
    statusOptions = [
      { key: 'go', label: s1Label },
      { key: 'fin', label: s2Label },
    ];
    console.log("[ROUTE_STATUS_BAR] using mode: 2, options:", statusOptions.map(o => o.label));
  } else if (routeStatesConfig.mode === '3') {
    const s1Label = routeStatesConfig.labelsFor3States?.state1 || 'Go';
    const s2Label = routeStatesConfig.labelsFor3States?.state2 || 'Salimos';
    const s3Label = routeStatesConfig.labelsFor3States?.state3 || 'Volvemos';
    statusOptions = [
      { key: 'go', label: s1Label },
      { key: 'salimos', label: s2Label },
      { key: 'fin', label: s3Label },
    ];
    console.log("[ROUTE_STATUS_BAR] using mode: 3, options:", statusOptions.map(o => o.label));
  } else {
    statusOptions = [
      { key: 'go', label: labels.go },
      { key: 'salimos', label: labels.salimos },
      { key: 'volvemos', label: labels.volvemos },
      { key: 'fin', label: labels.fin },
    ];
    console.log("[ROUTE_STATUS_BAR] using mode: 4, options:", statusOptions.map(o => o.label));
  }

  // Extraer el estado activo del perfil seleccionado
  // Usar UUID si existe, fallback a selectedUser para lectura de estado
  const effectiveProfileId = profileUuid || selectedUser;
   let profileStatus = null;
   if (currentStatus && typeof currentStatus === 'object') {
     profileStatus = effectiveProfileId
       ? currentStatus?.[effectiveProfileId] || null
       : null;
   } else if (typeof currentStatus === 'string') {
     profileStatus = currentStatus;
   }
   
   // [RT_CALC] Instrumentación temporal para diagnosticar cálculo de profileStatus
   console.log("[RT_CALC]", {
     selectedUser,
     profileUuid,
     effectiveProfileId,
     currentStatusKeys: Object.keys(currentStatus || {}),
     profileStatus
   });

  // ── Early return después de hooks y cálculos de estado ──
  if (!permissions.canUseRouteStates) return null;

  // ── Funciones (profileStatus y statusOptions ya disponibles) ──

  // Aplica el cambio real de estado + push event
  const applyStatusChange = async (status) => {
    const newStatus = profileStatus === status ? null : status;
    
    // Actualizar UI localmente primero (optimistic)
    onStatusChange(newStatus);

    // Disparar push event si el estado cambió a algo (no a null)
    if (newStatus) {
      try {
        if (workspaceId) {
          const option = statusOptions.find(o => o.key === newStatus);
          const label = option ? option.label : newStatus;
          const senderDeviceId = getOrCreateDeviceId();
          await createStatusPushEvent(workspaceId, newStatus, label, senderDeviceId, profileName);
          console.log('[ROUTE_STATUS_BAR] Push event created for status:', newStatus, 'from device:', senderDeviceId);
        }
      } catch (error) {
        console.error('[ROUTE_STATUS_BAR] Error creating push event:', error);
        // No fallar — el estado ya se cambió localmente
      }
    }
  };

  // Manejador de click en botón de estado
  const handleStatusClickWrapper = async (status) => {
    if (!requireConfirmation) {
      // Sin confirmación: comportamiento actual directo
      await applyStatusChange(status);
      return;
    }

    // Con confirmación activa:
    if (pendingRouteStatus === status) {
      // Mismo estado pendiente → cancelar confirmación
      setPendingRouteStatus(null);
      return;
    }

    // Otro estado (o ningún pendiente) → mostrar confirmación para este estado
    // Si había otro estado pendiente, se cancela automáticamente al sobreescribir
    setPendingRouteStatus(status);
  };

  // Confirmar: aplicar el cambio real
  // IMPORTANTE: limpiar pendingRouteStatus ANTES de applyStatusChange para evitar
  // que un re-render intermedio (optimistic update de onStatusChange) evalúe
  // pendingIsActiveStatus como true y muestre brevemente "¿Quitar estado X?".
  const handleConfirmRouteStatus = async () => {
    if (pendingRouteStatus) {
      const statusToApply = pendingRouteStatus;
      setPendingRouteStatus(null);        // Limpiar primero → ningún render verá el prompt
      await applyStatusChange(statusToApply);  // Luego aplicar el cambio real
    }
  };

  // Cancelar: no hacer nada
  const handleCancelRouteStatus = () => {
    setPendingRouteStatus(null);
  };

  // Label del estado pendiente (para mostrar en el prompt)
  const pendingOption = pendingRouteStatus
    ? statusOptions.find(o => o.key === pendingRouteStatus)
    : null;
  const pendingLabel = pendingOption?.label || pendingRouteStatus || '';
  // El estado pendiente es el que ya está activo → texto "¿Quitar estado X?"
  const pendingIsActiveStatus = pendingRouteStatus === profileStatus;

  return (
    <div className="w-full px-4 py-0">
      <div className="flex gap-2 justify-center">
        {statusOptions.map((option) => {
          const isActive = profileStatus === option.key;
          const isPendingThis = pendingRouteStatus === option.key;

           return (
             <button
               key={option.key}
               onClick={() => handleStatusClickWrapper(option.key)}
                className={cn(
                  'rutapp-btn flex-1 rounded-full px-3 py-1.5 text-sm font-medium border border-primary bg-card text-foreground transition-none',
                  // Resaltar el botón pendiente de confirmación
                  isPendingThis && 'ring-2 ring-amber-500 border-amber-500'
                )}
               style={
                 isActive && !pendingRouteStatus
                   ? { backgroundColor: getDisplayColor(option.key, theme, statusColors) }
                   : undefined
               }
             >
               {option.label}
             </button>
           );
        })}
      </div>

      {/* Prompt de confirmación — solo visible cuando hay un estado pendiente */}
      {pendingRouteStatus && (
        <div className="flex items-center justify-center gap-2 mt-2">
          <span className="text-sm font-semibold text-amber-800 dark:text-amber-200 text-center select-none">
            {pendingIsActiveStatus
              ? `¿Quitar estado ${pendingLabel}?`
              : `¿Cambiar estado a ${pendingLabel}?`}
          </span>

          {/* Botón confirmar (tick verde) */}
          <button
            type="button"
            onClick={handleConfirmRouteStatus}
            className="flex-shrink-0 h-8 w-8 rounded-full bg-green-500 hover:bg-green-600 active:bg-green-700 text-white flex items-center justify-center transition-colors"
            aria-label="Confirmar cambio de estado"
          >
            <Check className="h-4 w-4" />
          </button>

          {/* Botón cancelar (aspa roja) */}
          <button
            type="button"
            onClick={handleCancelRouteStatus}
            className="flex-shrink-0 h-8 w-8 rounded-full bg-red-500 hover:bg-red-600 active:bg-red-700 text-white flex items-center justify-center transition-colors"
            aria-label="Cancelar cambio de estado"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}


